import { GeoJson } from '@ais-ui/api-spec/shapes';

// Define interfaces for our data structures
interface Position {
  coordinates: [number, number]; // GeoJSON coordinates are [longitude, latitude]
}

interface TrackPoint {
  bs_ts: string;
  position: Position;
  name?: string;
  imo?: string | null;
  type_ship_cargo?: string | null;
  sog?: number;
  draught?: number;
  destination?: string;
  eta?: string;
  gps_lost?: boolean;
}

interface Track {
  mmsi: number;
  data: TrackPoint[];
}

interface VesselInfo {
  mmsi: number;
  name: string;
  imo: string | null;
  type_ship_cargo: string | null;
}

interface TrackGeoJSONResult {
  lineGeoJSON: GeoJson | null;
  pointsGeoJSON: GeoJson | null;
}

interface TracksGeoJSONResult {
  linesGeoJSON: GeoJson | null;
  pointsGeoJSON: GeoJson | null;
}

/**
 * Converts a single vessel track to separate GeoJSON objects for lines and points
 * @param {Track} track - Single track object with vessel data and position points
 * @returns {TrackGeoJSONResult} Object containing lineGeoJSON and pointsGeoJSON
 */
export const singleTrackToGeoJSON = (track: Track): TrackGeoJSONResult => {
  if (
    !track ||
    !track.data ||
    !Array.isArray(track.data) ||
    track.data.length === 0
  ) {
    return { lineGeoJSON: null, pointsGeoJSON: null };
  }

  const { mmsi, data } = track;

  // Sort by timestamp to ensure correct line sequence
  const sortedData = [...data].sort(
    (a, b) => new Date(a.bs_ts).getTime() - new Date(b.bs_ts).getTime(),
  );

  // Extract vessel info from first point
  const vesselInfo: VesselInfo = {
    mmsi,
    name: sortedData[0]?.name || `Unknown (${mmsi})`,
    imo: sortedData[0]?.imo || null,
    type_ship_cargo: sortedData[0]?.type_ship_cargo || null,
  };

  // Create LineString for the track
  const lineCoordinates: [number, number][] = sortedData
    .filter((point) => point.position && point.position.coordinates)
    .map((point) => point.position.coordinates);

  // Initialize lineGeoJSON to null
  let lineGeoJSON: GeoJson | null = null;

  if (lineCoordinates.length > 1) {
    // Create a LineString feature for this track
    lineGeoJSON = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: lineCoordinates,
      },
      properties: {
        ...vesselInfo,
        id: `gps-lost-track-line-${mmsi}`,
        type: 'gps-lost-track-linestring',
        pointCount: lineCoordinates.length,
        hasGpsLost: sortedData.some((point) => point.gps_lost),
        timestamp: new Date().toISOString(),
      },
    };
  }

  // Create Point features for each position in this track
  const pointFeatures: GeoJson[] = sortedData
    .filter((point) => point.position && point.position.coordinates)
    .map((point, index, filteredArray) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: point.position.coordinates,
      },
      properties: {
        ...vesselInfo,
        id: `gps-lost-track-point-${mmsi}-${index}`,
        type: 'gps-lost-track-point',
        timestamp: point.bs_ts,
        sog: point.sog,
        draught: point.draught,
        destination: point.destination,
        eta: point.eta,
        pointIndex: index,
        isGpsLost: point.gps_lost || false,
        isFirstPoint: index === 0,
        isLastPoint: index === filteredArray.length - 1,
      },
    }));

  // Create a FeatureCollection for this track's points
  const pointsGeoJSON: GeoJson | null =
    pointFeatures.length > 0
      ? {
          type: 'FeatureCollection',
          features: pointFeatures,
          properties: {
            id: `gps-lost-track-points-${mmsi}`,
            type: 'gps-lost-track-point',
            mmsi: mmsi,
            count: pointFeatures.length,
            timestamp: new Date().toISOString(),
          },
        }
      : null;

  return {
    lineGeoJSON,
    pointsGeoJSON,
  };
};

/**
 * Converts multiple vessel tracks to GeoJSON objects
 * @param {Track[]} tracks - Array of track objects with vessel data
 * @returns {TracksGeoJSONResult} Object containing all lines and points as GeoJSON FeatureCollections
 */
export const tracksToGeoJSON = (tracks: Track[]): TracksGeoJSONResult => {
  if (!tracks || !Array.isArray(tracks) || tracks.length === 0) {
    return {
      linesGeoJSON: null,
      pointsGeoJSON: null,
    };
  }

  // Initialize collections
  const lineFeatures: GeoJson[] = [];
  const pointFeatures: GeoJson[] = [];

  // Process each track
  tracks.forEach((track) => {
    const { lineGeoJSON, pointsGeoJSON } = singleTrackToGeoJSON(track);

    if (lineGeoJSON) {
      lineFeatures.push(lineGeoJSON);
    }

    if (pointsGeoJSON && pointsGeoJSON.features) {
      pointFeatures.push(...pointsGeoJSON.features);
    }
  });

  // Create FeatureCollection for lines
  const linesGeoJSON: GeoJson | null =
    lineFeatures.length > 0
      ? {
          type: 'FeatureCollection',
          features: lineFeatures,
          properties: {
            id: 'gps-lost-track-lines',
            type: 'gps-lost-track',
            count: lineFeatures.length,
            timestamp: new Date().toISOString(),
          },
        }
      : null;

  // Create FeatureCollection for points
  const pointsGeoJSON: GeoJson | null =
    pointFeatures.length > 0
      ? {
          type: 'FeatureCollection',
          features: pointFeatures,
          properties: {
            id: 'gps-lost-track-points',
            type: 'gps-lost-track-point',
            count: pointFeatures.length,
            timestamp: new Date().toISOString(),
          },
        }
      : null;

  return {
    linesGeoJSON,
    pointsGeoJSON,
  };
};
